/**
 * Documentation Registry
 * 
 * Central registry for all documentation pages.
 * Provides search indexing and navigation tree generation.
 */
import type { DocPage, DocSearchResult, DocNavNode, DocCategory, DocCategoryConfig, DocSectionConfig } from './types';

// Import all doc content
import { gettingStartedDocs } from './content/gettingStarted';
import { apiDocs } from './content/api';
import { paramsDocs } from './content/params';
import { typesDocs } from './content/types';
import { engineDocs } from './content/engine';
import { runsDocs } from './content/runs';
import { errorsDocs } from './content/errors';
import { examplesDocs } from './content/examples';
import { guidesDocs } from './content/guides';
import { dashboardDocs } from './content/dashboard';
import { backtestingDocs } from './content/backtesting';
import { strategyOptimizationDocs } from './content/strategyOptimization';
import { conceptsDocs } from './content/concepts';


/**
 * Section configuration — top-level groupings in sidebar
 */
export const SECTION_CONFIG: DocSectionConfig[] = [
  { id: 'learn', label: 'Learn', order: 1 },
  { id: 'platform', label: 'Using the Platform', order: 2 },
  { id: 'reference', label: 'Code Reference', order: 3 },
];

/**
 * Category configuration for display
 */
export const CATEGORY_CONFIG: DocCategoryConfig[] = [
  {
    id: 'getting-started',
    label: 'Getting Started',
    description: 'Introduction and core concepts',
    order: 1,
    section: 'learn',
  },
  {
    id: 'examples',
    label: 'Examples',
    description: 'Working strategy examples',
    order: 2,
    section: 'learn',
  },
  {
    id: 'guides',
    label: 'Best Practices',
    description: 'Patterns and anti-patterns',
    order: 3,
    section: 'learn',
  },
  {
    id: 'dashboard',
    label: 'Dashboard',
    description: 'Platform overview and navigation',
    order: 1,
    section: 'platform',
  },
  {
    id: 'backtesting',
    label: 'Backtesting',
    description: 'Running and analyzing backtests',
    order: 2,
    section: 'platform',
  },
  {
    id: 'strategy-optimization',
    label: 'Optimization',
    description: 'Parameter optimization and Walk-Forward Analysis',
    order: 3,
    section: 'platform',
  },

  {
    id: 'api',
    label: 'Strategy API',
    description: 'Function-level API reference',
    order: 1,
    section: 'reference',
  },
  {
    id: 'params',
    label: 'Parameters',
    description: 'Parameter system and optimization',
    order: 2,
    section: 'reference',
  },
  {
    id: 'types',
    label: 'Type Reference',
    description: 'Data types and interfaces',
    order: 3,
    section: 'reference',
  },
  {
    id: 'errors',
    label: 'Errors',
    description: 'Error codes and diagnostics',
    order: 4,
    section: 'reference',
  },
  {
    id: 'engine',
    label: 'Backtest Engine',
    description: 'Execution model and assumptions',
    order: 5,
    section: 'reference',
  },
  {
    id: 'runs',
    label: 'Run Modes',
    description: 'Backtest, optimization, WFA',
    order: 6,
    section: 'reference',
  },
];

/**
 * All documentation pages indexed by ID
 */
export const docRegistry = new Map<string, DocPage>();

// Register all docs
const allDocs: DocPage[] = [
  ...gettingStartedDocs,
  ...apiDocs,
  ...paramsDocs,
  ...typesDocs,
  ...engineDocs,
  ...runsDocs,
  ...errorsDocs,
  ...examplesDocs,
  ...guidesDocs,
  ...dashboardDocs,
  ...backtestingDocs,
  ...strategyOptimizationDocs,
  ...conceptsDocs,
];

allDocs.forEach((doc) => {
  docRegistry.set(doc.id, doc);
});

/**
 * Simple search implementation
 * Searches title, tags, description, and content
 */
export function searchDocs(query: string): DocSearchResult[] {
  const normalizedQuery = query.toLowerCase().trim();
  if (normalizedQuery.length < 2) return [];

  const results: DocSearchResult[] = [];
  const queryTerms = normalizedQuery.split(/\s+/);

  docRegistry.forEach((doc) => {
    let score = 0;
    let snippet: string | undefined;

    // Title match (highest weight)
    if (doc.title.toLowerCase().includes(normalizedQuery)) {
      score += 100;
    }
    queryTerms.forEach((term) => {
      if (doc.title.toLowerCase().includes(term)) {
        score += 20;
      }
    });

    // Tag match (high weight)
    doc.tags.forEach((tag) => {
      if (tag.toLowerCase().includes(normalizedQuery)) {
        score += 50;
      }
      queryTerms.forEach((term) => {
        if (tag.toLowerCase().includes(term)) {
          score += 10;
        }
      });
    });

    // Description match (medium weight)
    if (doc.description?.toLowerCase().includes(normalizedQuery)) {
      score += 30;
      snippet = doc.description;
    }

    // Content match (lower weight, extract snippet)
    const contentLower = doc.content.toLowerCase();
    if (contentLower.includes(normalizedQuery)) {
      score += 15;
      // Extract snippet around match
      const matchIndex = contentLower.indexOf(normalizedQuery);
      const start = Math.max(0, matchIndex - 40);
      const end = Math.min(doc.content.length, matchIndex + normalizedQuery.length + 60);
      snippet = (start > 0 ? '...' : '') + 
                doc.content.slice(start, end).replace(/\n/g, ' ') + 
                (end < doc.content.length ? '...' : '');
    }

    if (score > 0) {
      results.push({
        id: doc.id,
        title: doc.title,
        category: doc.category,
        subcategory: doc.subcategory,
        description: doc.description,
        snippet,
        score,
      });
    }
  });

  // Sort by score descending
  return results.sort((a, b) => b.score - a.score).slice(0, 20);
}

/**
 * Generate navigation tree from registered docs, grouped by section
 */
export function getNavTree(): DocNavNode[] {
  // Group docs by category, then subcategory
  const byCategory = new Map<string, Map<string, DocPage[]>>();
  
  docRegistry.forEach((doc) => {
    if (!byCategory.has(doc.category)) {
      byCategory.set(doc.category, new Map());
    }
    const categoryMap = byCategory.get(doc.category)!;
    const subcat = doc.subcategory || '_root';
    if (!categoryMap.has(subcat)) {
      categoryMap.set(subcat, []);
    }
    categoryMap.get(subcat)!.push(doc);
  });

  // Build category node from config
  const buildCategoryNode = (catConfig: DocCategoryConfig): DocNavNode | null => {
    const categoryDocs = byCategory.get(catConfig.id);
    if (!categoryDocs || categoryDocs.size === 0) return null;

    const categoryNode: DocNavNode = {
      id: catConfig.id,
      title: catConfig.label,
      type: 'category',
      children: [],
    };

    const subcats = Array.from(categoryDocs.keys()).sort((a, b) => {
      if (a === '_root') return -1;
      if (b === '_root') return 1;
      return a.localeCompare(b);
    });

    subcats.forEach((subcat) => {
      const docs = categoryDocs.get(subcat)!;
      docs.sort((a, b) => (a.order || 0) - (b.order || 0));

      if (subcat === '_root') {
        docs.forEach((doc) => {
          categoryNode.children!.push({
            id: doc.id,
            title: doc.title,
            type: 'page',
            docId: doc.id,
          });
        });
      } else {
        const subcatNode: DocNavNode = {
          id: `${catConfig.id}/${subcat}`,
          title: subcat.charAt(0).toUpperCase() + subcat.slice(1),
          type: 'subcategory',
          children: docs.map((doc) => ({
            id: doc.id,
            title: doc.title,
            type: 'page' as const,
            docId: doc.id,
          })),
        };
        categoryNode.children!.push(subcatNode);
      }
    });

    return categoryNode;
  };

  // Group categories by section
  const tree: DocNavNode[] = [];

  SECTION_CONFIG.forEach((sectionConfig) => {
    const sectionCategories = CATEGORY_CONFIG
      .filter((c) => c.section === sectionConfig.id)
      .sort((a, b) => a.order - b.order);

    const sectionChildren: DocNavNode[] = [];
    sectionCategories.forEach((catConfig) => {
      const node = buildCategoryNode(catConfig);
      if (node) sectionChildren.push(node);
    });

    if (sectionChildren.length > 0) {
      tree.push({
        id: `section-${sectionConfig.id}`,
        title: sectionConfig.label,
        type: 'section',
        children: sectionChildren,
      });
    }
  });

  return tree;
}

/**
 * Get a doc by ID
 */
export function getDoc(id: string): DocPage | undefined {
  return docRegistry.get(id);
}

/**
 * Get category config by ID
 */
export function getCategoryConfig(id: DocCategory): DocCategoryConfig | undefined {
  return CATEGORY_CONFIG.find((c) => c.id === id);
}
